import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, useOverlay } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { useGroupLimits } from '../../../providers/GroupLimitsProvider'
import ViewTreeDetailsOverlay from '../../gedcom-upload/ViewTreeDetailsOverlay'

const CreateTreeDialog = lazy(() => import('../../gedcom-upload/CreateTreeDialog'))

/**
 * Button that handles uploading a gedcom for a CET
 * @param {object} buttonProps - Any props you want to apply to the Button. `onClick` and `Icon` are overwritten.
 * @param {number | undefined} groupsCount - Count of user's groups.
 * @param {Function} onUploadGedcom - A callback function that is called as GroupList successfully reloads the list as a result of creating a new group. Parameter: (groupId)
 */

export default function UploadGedcomButton({ groupsCount, onUploadGedcom }) {
  const [t] = useTranslation()
  const focusBackRef = useRef(null)
  const createTreeOverlay = useOverlay()
  const createdTreeStateRef = useRef('notSet')
  const hasOpenedCreateTreeOverlay = useRef(false)
  const viewTreeDetailsOverlay = useOverlay({ focusBackRef })
  const [fileInfo, setFileInfo] = useState({})
  const { maxGroupsPerUser, loading: loadingGroupLimits } = useGroupLimits()

  const onSuccess = useCallback((data) => {
    createdTreeStateRef.current = 'createdTree'
    setFileInfo(data)
  }, [])

  useEffect(() => {
    if (
      !createTreeOverlay.isOpen &&
      !createTreeOverlay.transitioning &&
      createdTreeStateRef.current === 'createdTree' &&
      fileInfo?.title
    ) {
      viewTreeDetailsOverlay.open()
      createdTreeStateRef.current = 'openedViewTreeDetails'
    }
  }, [createTreeOverlay.isOpen, createTreeOverlay.transitioning, fileInfo?.title, viewTreeDetailsOverlay])

  useEffect(() => {
    if (
      !viewTreeDetailsOverlay.isOpen &&
      !viewTreeDetailsOverlay.transitioning &&
      createdTreeStateRef.current !== 'notSet'
    ) {
      onUploadGedcom()
      createdTreeStateRef.current = 'notSet'
    }
  }, [onUploadGedcom, viewTreeDetailsOverlay, viewTreeDetailsOverlay.isOpen, viewTreeDetailsOverlay.transitioning])

  useEffect(() => {
    if (createTreeOverlay.isOpen && !createTreeOverlay.transitioning && !hasOpenedCreateTreeOverlay.current) {
      hasOpenedCreateTreeOverlay.current = true
    }
  }, [createTreeOverlay])

  useEffect(() => {
    if (!createTreeOverlay.isOpen && !createTreeOverlay.transitioning && hasOpenedCreateTreeOverlay.current) {
      hasOpenedCreateTreeOverlay.current = false
      onUploadGedcom?.()
    }
  }, [onUploadGedcom, createTreeOverlay, createTreeOverlay.isOpen, createTreeOverlay.transitioning])

  const loading = loadingGroupLimits || !Number.isInteger(groupsCount)
  // Check length of groups to disable button when group limit has been reached
  const overLimit =
    Number.isInteger(maxGroupsPerUser) && Number.isInteger(groupsCount) ? groupsCount >= maxGroupsPerUser : undefined

  return (
    <>
      <Button
        ref={focusBackRef}
        data-testid="upload-gedcom-button.upload-gedcom.button"
        disabled={loading || overLimit}
        Icon={ContentAdd}
        onClick={createTreeOverlay.handleClick}
        linkName="Upload Gedcom Button"
        emphasis="medium"
        title={
          overLimit ? t('group-management.create-group-max-groups.error', { maxGroups: maxGroupsPerUser }) : undefined
        }
      >
        {t('group-management.upload-gedcom.action')}
      </Button>

      <Suspense fallback="">
        {!overLimit && !loadingGroupLimits && (createTreeOverlay.isOpen || createTreeOverlay.transitioning) && (
          <CreateTreeDialog overlay={createTreeOverlay} onSuccess={onSuccess} />
        )}
      </Suspense>

      {(viewTreeDetailsOverlay.isOpen || viewTreeDetailsOverlay.transitioning) && (
        <ViewTreeDetailsOverlay fileInfo={fileInfo} overlay={viewTreeDetailsOverlay} />
      )}
    </>
  )
}
